import { create } from "zustand";
import { apiFetch } from "#/utils/api";

export interface AuthUser {
	id: string;
	email: string;
	name: string;
	roles: string[];
}

export interface AuthState {
	user: AuthUser | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	login: () => Promise<void>;
	logout: () => Promise<void>;
	checkAuth: () => Promise<void>;
}

async function refreshToken(): Promise<boolean> {
	try {
		const response = await apiFetch("/auth/refresh", {
			method: "POST",
		});
		return response.ok;
	} catch {
		return false;
	}
}

/**
 * Zustand store for authentication state and actions.
 */
export const useAuthStore = create<AuthState>((set, get) => ({
	user: null,
	isAuthenticated: false,
	isLoading: true,
	checkAuth: async () => {
		set({ isLoading: true });
		try {
			let response = await apiFetch("/auth/me");

			if (response.status === 401) {
				const refreshed = await refreshToken();
				if (refreshed) {
					response = await apiFetch("/auth/me");
				}
			}

			if (response.ok) {
				const data = (await response.json()) as { user: AuthUser };
				set({ user: data.user, isAuthenticated: true, isLoading: false });
				return;
			}

			set({ user: null, isAuthenticated: false, isLoading: false });
		} catch {
			set({ user: null, isAuthenticated: false, isLoading: false });
		}
	},
	login: async () => {
		await get().checkAuth();
	},
	logout: async () => {
		try {
			await apiFetch("/auth/logout", {
				method: "POST",
			});
		} catch {
			// Still clear user on error
		}
		set({ user: null, isAuthenticated: false, isLoading: false });
	},
}));
